import * as React from "react";
import { useState } from "react";
import { ForboIcon, Group } from "../../types/models";
import IconPickerGrid from "./IconPickerGrid";
import IconPickerInsertBar from "./IconPickerInsertBar";
import IconPickerSearch from "./IconPickerSearch";
import IconPickerTabs from "./IconPickerTabs";
import { useIconPickerStyles } from "./iconPickerStyles";

interface Props {
  groups: Group[];
  onInsert: (icons: ForboIcon[]) => Promise<boolean>;
}

const IconPicker: React.FC<Props> = ({ groups, onInsert }) => {
  const styles = useIconPickerStyles();
  const [search, setSearch] = useState("");
  const [activeGroup, setActiveGroup] = useState(groups[0]?.group || "");
  const [selectedIcons, setSelectedIcons] = useState<Set<string>>(new Set());
  const [isInserting, setIsInserting] = useState(false);

  React.useEffect(() => {
    if (!groups.some((group) => group.group === activeGroup)) {
      setActiveGroup(groups[0]?.group || "");
    }
  }, [activeGroup, groups]);

  const filteredGroups = groups.map((group) => ({
    ...group,
    icons: group.icons.filter((icon) => icon.title.toLowerCase().includes(search.toLowerCase())),
  }));

  const activeIcons = filteredGroups
    .filter((group) => group.group === activeGroup)
    .flatMap((group) => group.icons);

  const toggleIcon = (iconId: string) => {
    if (isInserting) {
      return;
    }

    setSelectedIcons((previous) => {
      const next = new Set(previous);
      if (next.has(iconId)) {
        next.delete(iconId);
      } else {
        next.add(iconId);
      }

      return next;
    });
  };

  const handleInsert = async () => {
    const iconsToInsert = groups
      .flatMap((group) => group.icons)
      .filter((icon) => selectedIcons.has(icon.id));

    if (iconsToInsert.length === 0) {
      return;
    }

    setIsInserting(true);

    try {
      const inserted = await onInsert(iconsToInsert);
      if (inserted) {
        setSelectedIcons(new Set());
      }
    } finally {
      setIsInserting(false);
    }
  };

  return (
    <div className={styles.container}>
      <IconPickerSearch search={search} onClear={() => setSearch("")} onSearchChange={setSearch} />
      <IconPickerTabs activeGroup={activeGroup} groups={groups} onSelectGroup={setActiveGroup} />
      <IconPickerGrid
        icons={activeIcons}
        isInserting={isInserting}
        selectedIcons={selectedIcons}
        onToggleIcon={toggleIcon}
      />
      <IconPickerInsertBar
        isInserting={isInserting}
        selectedCount={selectedIcons.size}
        onInsert={handleInsert}
      />
    </div>
  );
};

export default IconPicker;
